import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, Check, ArrowRight, Zap, Calendar, Receipt, 
  RefreshCw, AlertCircle, ChevronDown, ExternalLink, Globe,
  IndianRupee, DollarSign, PoundSterling, Banknote
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency configuration with pricing
const CURRENCIES = {
  INR: { 
    code: 'INR', 
    symbol: '₹', 
    flag: '🇮🇳', 
    name: 'Indian Rupee',
    icon: IndianRupee,
    multiplier: 1, // Base currency
    locale: 'en-IN'
  },
  USD: { 
    code: 'USD', 
    symbol: '$', 
    flag: '🇺🇸', 
    name: 'US Dollar',
    icon: DollarSign,
    multiplier: 0.012, // Approx conversion
    locale: 'en-US'
  },
  GBP: { 
    code: 'GBP', 
    symbol: '£', 
    flag: '🇬🇧', 
    name: 'British Pound',
    icon: PoundSterling,
    multiplier: 0.0095,
    locale: 'en-GB'
  },
  AED: { 
    code: 'AED', 
    symbol: 'د.إ', 
    flag: '🇦🇪', 
    name: 'UAE Dirham',
    icon: Banknote,
    multiplier: 0.044,
    locale: 'ar-AE'
  },
  SGD: { 
    code: 'SGD', 
    symbol: 'S$', 
    flag: '🇸🇬', 
    name: 'Singapore Dollar',
    icon: DollarSign,
    multiplier: 0.016,
    locale: 'en-SG'
  }
};

// Fixed pricing in each currency (monthly values, yearly = 10 months worth for 2 months free)
const PRICING = {
  free: {
    INR: { monthly: 0, yearly: 0 },
    USD: { monthly: 0, yearly: 0 },
    GBP: { monthly: 0, yearly: 0 },
    AED: { monthly: 0, yearly: 0 },
    SGD: { monthly: 0, yearly: 0 }
  },
  starter: {
    INR: { monthly: 999, yearly: 9990 },
    USD: { monthly: 12, yearly: 120 },
    GBP: { monthly: 10, yearly: 100 },
    AED: { monthly: 45, yearly: 450 },
    SGD: { monthly: 16, yearly: 160 }
  },
  growth: {
    INR: { monthly: 2499, yearly: 24990 },
    USD: { monthly: 30, yearly: 300 },
    GBP: { monthly: 24, yearly: 240 },
    AED: { monthly: 110, yearly: 1100 },
    SGD: { monthly: 40, yearly: 400 }
  },
  agency: {
    INR: { monthly: 5999, yearly: 59990 },
    USD: { monthly: 72, yearly: 720 },
    GBP: { monthly: 57, yearly: 570 },
    AED: { monthly: 265, yearly: 2650 },
    SGD: { monthly: 96, yearly: 960 }
  },
  enterprise: {
    INR: { monthly: null, yearly: null },
    USD: { monthly: null, yearly: null },
    GBP: { monthly: null, yearly: null },
    AED: { monthly: null, yearly: null },
    SGD: { monthly: null, yearly: null }
  }
};

// Auto-detect currency based on timezone/locale
const detectCurrency = () => {
  const savedCurrency = localStorage.getItem('preferred_currency');
  if (savedCurrency && CURRENCIES[savedCurrency]) {
    return savedCurrency;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language || navigator.userLanguage;
    
    // India detection
    if (timezone?.includes('Kolkata') || timezone?.includes('Asia/Calcutta') || 
        language?.startsWith('hi') || language === 'en-IN') {
      return 'INR';
    }
    
    // UAE detection
    if (timezone?.includes('Dubai') || timezone?.includes('Asia/Dubai') ||
        language?.startsWith('ar-AE')) {
      return 'AED';
    }
    
    // UK detection
    if (timezone?.includes('London') || timezone?.includes('Europe/London') ||
        language === 'en-GB') {
      return 'GBP';
    }
    
    // Singapore detection
    if (timezone?.includes('Singapore') || language === 'en-SG') {
      return 'SGD';
    }
    
    // Default to USD for others
    return 'USD';
  } catch {
    return 'USD';
  }
};

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [config, setConfig] = useState({ configured: false, key_id: '' });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPayments, setShowPayments] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => detectCurrency());
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  const token = localStorage.getItem('auth_token');
  const currencyInfo = CURRENCIES[selectedCurrency];

  useEffect(() => {
    fetchData();
    loadRazorpayScript();
  }, []);

  // Save currency preference
  useEffect(() => {
    localStorage.setItem('preferred_currency', selectedCurrency);
  }, [selectedCurrency]);

  const loadRazorpayScript = () => {
    if (document.getElementById('razorpay-script')) return;
    
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      const [plansRes, configRes, subRes, paymentsRes, usageRes, billingUsageRes] = await Promise.all([
        fetch(`${API_URL}/api/billing/plans`, { headers }),
        fetch(`${API_URL}/api/billing/config`, { headers }),
        fetch(`${API_URL}/api/billing/subscription`, { headers }),
        fetch(`${API_URL}/api/billing/payment-history`, { headers }),
        fetch(`${API_URL}/api/ai/usage`, { headers }),
        fetch(`${API_URL}/api/billing/usage`, { headers })
      ]);

      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data.plans || []);
      }

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
      }

      if (subRes.ok) {
        const data = await subRes.json();
        setSubscription(data);
      }

      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setPayments(data.payments || []);
      }

      // Merge AI usage and billing usage
      let mergedUsage = {};
      if (usageRes.ok) {
        const aiData = await usageRes.json();
        mergedUsage = { ...aiData };
      }
      if (billingUsageRes.ok) {
        const billingData = await billingUsageRes.json();
        mergedUsage = { ...mergedUsage, ...billingData };
      }
      setUsage(mergedUsage);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (planId, cycle) => {
    const pricing = PRICING[planId];
    if (!pricing) return 0;
    
    const currencyPricing = pricing[selectedCurrency];
    if (!currencyPricing) return 0;
    
    return cycle === 'yearly' ? currencyPricing.yearly : currencyPricing.monthly;
  };

  const getINRPrice = (planId, cycle) => {
    const pricing = PRICING[planId];
    if (!pricing) return 0;
    return cycle === 'yearly' ? pricing.INR.yearly : pricing.INR.monthly;
  };

  const formatPrice = (amount, currency = selectedCurrency) => {
    const curr = CURRENCIES[currency];
    // Amount is already in whole units (not paise/cents)
    return `${curr.symbol}${amount.toLocaleString(curr.locale)}`;
  };

  const handleSubscribe = async (planId) => {
    if (!config.configured) {
      toast.error('Payment gateway not configured. Please contact support.');
      return;
    }

    setProcessingPlan(planId);
    
    // Always charge in INR via Razorpay
    const inrAmount = getINRPrice(planId, billingCycle) * 100; // Convert to paise
    
    try {
      // Create order in INR
      const orderRes = await fetch(`${API_URL}/api/billing/create-subscription-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          plan: planId, 
          billing_cycle: billingCycle,
          amount_override: inrAmount // Always INR
        })
      });

      if (!orderRes.ok) {
        const error = await orderRes.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await orderRes.json();

      // Prepare description based on currency
      let description = `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan - ${billingCycle}`;
      if (selectedCurrency !== 'INR') {
        description += ` (Displayed: ${formatPrice(getPrice(planId, billingCycle))})`;
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency, // Always INR
        order_id: orderData.order_id,
        name: 'AI Lead Mantra',
        description,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API_URL}/api/billing/verify-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan: planId
              })
            });

            if (verifyRes.ok) {
              toast.success('Payment successful! Your subscription is now active.');
              fetchData();
            } else {
              const error = await verifyRes.json();
              toast.error(error.detail || 'Payment verification failed');
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#10b981'
        },
        modal: {
          ondismiss: () => {
            setProcessingPlan(null);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setProcessingPlan(null);
      });
      rzp.open();

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to process subscription');
    } finally {
      setProcessingPlan(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatPaymentAmount = (amount) => {
    return `₹${(amount / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6" data-testid="billing-page">
      {/* Header with Currency Selector */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Billing & Subscription</h1>
          <p className="text-emerald-300/70">Manage your subscription and payment history</p>
        </div>

        {/* Currency Selector */}
        <div className="relative">
          <button
            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
            className="flex items-center gap-2 px-4 py-2 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg text-white hover:bg-[#0a3d2e] transition-colors"
            data-testid="currency-selector"
          >
            <Globe className="w-4 h-4 text-emerald-400" />
            <span className="text-lg">{currencyInfo.flag}</span>
            <span>{currencyInfo.code}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showCurrencyDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 mt-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden"
              >
                {Object.values(CURRENCIES).map((currency) => (
                  <button
                    key={currency.code}
                    onClick={() => {
                      setSelectedCurrency(currency.code);
                      setShowCurrencyDropdown(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition-colors ${
                      selectedCurrency === currency.code ? 'bg-emerald-900/30' : ''
                    }`}
                    data-testid={`currency-option-${currency.code}`}
                  >
                    <span className="text-xl">{currency.flag}</span>
                    <div className="flex-1 text-left">
                      <p className="text-white font-medium">{currency.code}</p>
                      <p className="text-sm text-gray-400">{currency.name}</p>
                    </div>
                    {selectedCurrency === currency.code && (
                      <Check className="w-4 h-4 text-emerald-400" />
                    )}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Coming Soon Banner for Stripe */}
      {selectedCurrency !== 'INR' && (
        <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
          <CardContent className="p-4 flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-300">💳 Direct {selectedCurrency} Payment Coming Soon!</h3>
              <p className="text-sm text-blue-200/80 mt-1">
                Stripe integration for {currencyInfo.name} is on the way. For now, you'll be charged in INR (₹).
                <br />
                <span className="text-blue-300">Your selected currency pricing will be converted to INR at checkout.</span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration Warning */}
      {!config.configured && (
        <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-300">Payment Gateway Not Configured</h3>
              <p className="text-sm text-amber-200/80 mt-1">
                Payment processing is not configured. Please contact support for assistance.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Subscription */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="bg-[#052e22]/50 border-emerald-800/30 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              Current Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-white capitalize">
                  {subscription?.plan || 'Free'}
                </p>
                <Badge className={`mt-2 ${
                  subscription?.status === 'active' 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-slate-500/20 text-emerald-300/70'
                }`}>
                  {subscription?.status || 'Inactive'}
                </Badge>
              </div>
              {subscription?.ends_at && (
                <div className="text-right">
                  <p className="text-sm text-emerald-300/70">Renews on</p>
                  <p className="text-lg font-medium text-white">
                    {formatDate(subscription.ends_at)}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* AI Usage Card */}
        {usage && (
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader>
              <CardTitle className="text-xl text-white">AI Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-emerald-300/70">Tokens Used</span>
                    <span className="text-sm text-white">
                      {usage.tokens_used?.toLocaleString() || 0} / {usage.tokens_limit?.toLocaleString() || 1000}
                    </span>
                  </div>
                  <div className="w-full bg-[#0a3d2e] rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((usage.tokens_used / (usage.tokens_limit || 1000)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Usage Tracking Section */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30 mb-8">
        <CardHeader>
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-400" />
            Usage This Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Leads Usage */}
            <div className="bg-[#0a3d2e]/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-emerald-300/70">Leads</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  (usage?.usage?.leads?.percent || 0) >= 90 ? 'bg-red-500/20 text-red-400' :
                  (usage?.usage?.leads?.percent || 0) >= 70 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {usage?.usage?.leads?.percent || 0}%
                </span>
              </div>
              <p className="text-2xl font-bold text-white">
                {usage?.usage?.leads?.current || 0}
                <span className="text-sm text-emerald-300/50 font-normal">
                  /{usage?.usage?.leads?.limit === -1 ? '∞' : usage?.usage?.leads?.limit || 50}
                </span>
              </p>
              <div className="w-full bg-[#0a3d2e] rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    (usage?.usage?.leads?.percent || 0) >= 90 ? 'bg-red-500' :
                    (usage?.usage?.leads?.percent || 0) >= 70 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(usage?.usage?.leads?.percent || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* Emails Usage */}
            <div className="bg-[#0a3d2e]/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-emerald-300/70">Emails</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  (usage?.usage?.emails?.percent || 0) >= 90 ? 'bg-red-500/20 text-red-400' :
                  (usage?.usage?.emails?.percent || 0) >= 70 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {usage?.usage?.emails?.percent || 0}%
                </span>
              </div>
              <p className="text-2xl font-bold text-white">
                {usage?.usage?.emails?.current || 0}
                <span className="text-sm text-emerald-300/50 font-normal">
                  /{usage?.usage?.emails?.limit === -1 ? '∞' : usage?.usage?.emails?.limit || 10}
                </span>
              </p>
              <div className="w-full bg-[#0a3d2e] rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    (usage?.usage?.emails?.percent || 0) >= 90 ? 'bg-red-500' :
                    (usage?.usage?.emails?.percent || 0) >= 70 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(usage?.usage?.emails?.percent || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* WhatsApp Usage */}
            <div className="bg-[#0a3d2e]/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-emerald-300/70">WhatsApp</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  (usage?.usage?.whatsapp?.percent || 0) >= 90 ? 'bg-red-500/20 text-red-400' :
                  (usage?.usage?.whatsapp?.percent || 0) >= 70 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {usage?.usage?.whatsapp?.limit === 0 ? 'N/A' : `${usage?.usage?.whatsapp?.percent || 0}%`}
                </span>
              </div>
              <p className="text-2xl font-bold text-white">
                {usage?.usage?.whatsapp?.current || 0}
                <span className="text-sm text-emerald-300/50 font-normal">
                  /{usage?.usage?.whatsapp?.limit === -1 ? '∞' : usage?.usage?.whatsapp?.limit || 0}
                </span>
              </p>
              <div className="w-full bg-[#0a3d2e] rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    (usage?.usage?.whatsapp?.percent || 0) >= 90 ? 'bg-red-500' :
                    (usage?.usage?.whatsapp?.percent || 0) >= 70 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(usage?.usage?.whatsapp?.percent || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* Voice AI Calls Usage */}
            <div className="bg-[#0a3d2e]/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-emerald-300/70">AI Calls</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  (usage?.usage?.calls?.percent || 0) >= 90 ? 'bg-red-500/20 text-red-400' :
                  (usage?.usage?.calls?.percent || 0) >= 70 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {usage?.usage?.calls?.limit === 0 ? 'N/A' : `${usage?.usage?.calls?.percent || 0}%`}
                </span>
              </div>
              <p className="text-2xl font-bold text-white">
                {usage?.usage?.calls?.current || 0}
                <span className="text-sm text-emerald-300/50 font-normal">
                  /{usage?.usage?.calls?.limit === -1 ? '∞' : usage?.usage?.calls?.limit || 0}
                </span>
              </p>
              <div className="w-full bg-[#0a3d2e] rounded-full h-1.5 mt-2">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    (usage?.usage?.calls?.percent || 0) >= 90 ? 'bg-red-500' :
                    (usage?.usage?.calls?.percent || 0) >= 70 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(usage?.usage?.calls?.percent || 0, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Warning for high usage */}
          {((usage?.usage?.leads?.percent || 0) >= 80 || (usage?.usage?.emails?.percent || 0) >= 80) && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-300 font-medium">You're approaching your plan limits</p>
                <p className="text-amber-200/70 text-sm">Consider upgrading to unlock more features and higher limits.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-[#0a3d2e]/50 p-1 rounded-xl inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-emerald-600 text-white'
                : 'text-emerald-300/70 hover:text-white'
            }`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              billingCycle === 'yearly'
                ? 'bg-emerald-600 text-white'
                : 'text-emerald-300/70 hover:text-white'
            }`}
            data-testid="billing-yearly"
          >
            Yearly <span className="text-emerald-400 ml-1">-17%</span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {['starter', 'growth', 'agency'].map((planId, index) => {
          const plan = plans.find(p => p.id === planId) || { 
            id: planId, 
            name: planId.charAt(0).toUpperCase() + planId.slice(1),
            features: planId === 'starter' 
              ? ['1,000 AI tokens', '500 leads', '1 funnel', 'Email support']
              : planId === 'growth'
              ? ['10,000 AI tokens', '5,000 leads', '10 funnels', 'Priority support', 'Voice AI']
              : ['100,000 AI tokens', 'Unlimited leads', 'Unlimited funnels', '24/7 support', 'Voice AI', 'White-label']
          };
          
          const displayPrice = getPrice(planId, billingCycle);
          const inrPrice = getINRPrice(planId, billingCycle);
          const isCurrentPlan = subscription?.plan === planId && subscription?.status === 'active';
          const isPopular = planId === 'growth';

          return (
            <motion.div
              key={planId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`relative bg-[#052e22]/50 border-emerald-800/30 ${
                isPopular ? 'border-emerald-500 ring-2 ring-emerald-500/20' : ''
              } ${isCurrentPlan ? 'border-emerald-500' : ''}`}
              data-testid={`plan-card-${planId}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-600 text-white">
                      <Zap className="w-3 h-3 mr-1" /> Most Popular
                    </Badge>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-emerald-500 text-white">
                      <Check className="w-3 h-3 mr-1" /> Current
                    </Badge>
                  </div>
                )}

                <CardContent className="p-6 pt-8">
                  <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-white">{formatPrice(displayPrice)}</span>
                    <span className="text-emerald-300/70">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  
                  {/* Show INR conversion for non-INR currencies */}
                  {selectedCurrency !== 'INR' && (
                    <p className="text-sm text-gray-400 mb-4">
                      ≈ ₹{inrPrice.toLocaleString('en-IN')} charged in INR
                    </p>
                  )}

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(planId)}
                    disabled={!config.configured || processingPlan === planId || isCurrentPlan}
                    className={`w-full ${
                      isPopular ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                    data-testid={`subscribe-btn-${planId}`}
                  >
                    {processingPlan === planId ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      <>
                        {selectedCurrency !== 'INR' ? (
                          <>
                            Pay in INR (₹) <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        ) : (
                          <>
                            Subscribe <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Payment History */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30">
        <CardHeader>
          <button
            onClick={() => setShowPayments(!showPayments)}
            className="w-full flex items-center justify-between"
            data-testid="payment-history-toggle"
          >
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-400" />
              Payment History
            </CardTitle>
            <ChevronDown className={`w-5 h-5 text-emerald-300/70 transition-transform ${showPayments ? 'rotate-180' : ''}`} />
          </button>
        </CardHeader>
        
        <AnimatePresence>
          {showPayments && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <CardContent>
                {payments.length === 0 ? (
                  <p className="text-emerald-300/70 text-center py-8">No payment history yet</p>
                ) : (
                  <div className="space-y-3">
                    {payments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between p-4 bg-[#0a3d2e]/50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-white capitalize">
                            {payment.plan || payment.type} Payment
                          </p>
                          <p className="text-sm text-emerald-300/70">
                            {formatDate(payment.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">
                            {formatPaymentAmount(payment.amount)}
                          </p>
                          <Badge className={`${
                            payment.status === 'captured' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : payment.status === 'refunded'
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {payment.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </div>
  );
}
